import type { MouseEventHandler } from 'react'
import React, { forwardRef } from 'react'
import type { PropsOf } from '../../system'
import { Tooltip } from '../TooltipV2'
import { MoteActionButtonRoot } from './MoteActionButton.styles'

export interface MoteActionButtonProps extends PropsOf<'button'> {
  onClick: MouseEventHandler<HTMLButtonElement>
  loading?: boolean
  text: string
  icon: React.ReactNode | string
  id: string
  ariaLabel: string
  textPosition: 'inside' | 'tooltip' | 'none'
  size?: 'sm' | 'md' | 'lg'
  forceTooltip?: boolean
  tooltipText?: string
}

export const MoteActionButton = forwardRef<
  HTMLButtonElement,
  MoteActionButtonProps
>((props, ref): JSX.Element => {
  const {
    text,
    icon,
    onClick,
    id,
    ariaLabel,
    textPosition,
    size = 'sm',
    forceTooltip,
    ...rest
  } = props

  const tooltip =
    textPosition === 'tooltip' ? (
      <Tooltip id={id} place={'top'}>
        {text}
      </Tooltip>
    ) : null

  const button = (
    <MoteActionButtonRoot
      {...(textPosition === 'tooltip'
        ? {
            'data-tip': true,
            'data-for': id
          }
        : undefined)}
      ref={ref}
      size={size}
      icon={icon}
      variant='secondary'
      onClick={onClick}
      aria-label={ariaLabel}
      {...rest}
      text={textPosition === 'inside' ? text : undefined}
      data-testid={id}
      block
    />
  )

  if (forceTooltip) {
    return (
      <div data-tip data-for={id}>
        {button}
        {tooltip}
      </div>
    )
  }

  return (
    <>
      {button}
      {tooltip}
    </>
  )
})
